/**
 * Routing, in twenty lines.
 *
 * There are two screens. A router dependency would be more code than the router, and a
 * hash route means a match link works from a static file server with no rewrite rules —
 * which matters because Caddy serves the client and the Deno process only answers `/api`
 * and `/ws`.
 */

import { useEffect, useState } from 'react';

export type Route =
  | { readonly name: 'lobby' }
  | { readonly name: 'learn' }
  | { readonly name: 'play' }
  | { readonly name: 'watch' }
  | { readonly name: 'match'; readonly id: string };

export function parseRoute(hash: string): Route {
  if (hash === '#/learn') return { name: 'learn' };
  if (hash === '#/play') return { name: 'play' };
  // Unlinked on purpose: two bots playing each other is a window onto the engine, not a
  // feature of the site. Reachable by typing the URL, which is who it is for.
  if (hash === '#/watch') return { name: 'watch' };
  const match = /^#\/m\/([0-9a-z]{4,40})$/.exec(hash);
  return match ? { name: 'match', id: match[1] } : { name: 'lobby' };
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(globalThis.location.hash));

  useEffect(() => {
    const update = () => setRoute(parseRoute(globalThis.location.hash));
    globalThis.addEventListener('hashchange', update);
    return () => globalThis.removeEventListener('hashchange', update);
  }, []);

  return route;
}

export const matchHref = (id: string): string => `#/m/${id}`;
export const LEARN_HREF = '#/learn';
export const PLAY_HREF = '#/play';
export const WATCH_HREF = '#/watch';

export function navigate(hash: string): void {
  globalThis.location.hash = hash;
}
